import React, { ChangeEvent, useCallback, useEffect } from 'react';
import { StyledUploadLink } from '../atoms/styles';
import { Button } from '@material-ui/core';
import { usePostUploadExecelStudyCardsMutation } from 'src/modules/shared/slices/study-card/api/studyCard';
import { useSnackbar } from 'notistack';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useGetPagesQuery } from 'src/modules/shared/slices/page/api/pages';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { trajectorySectionCodeLookup } from 'src/config/api/models/Trajectory';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const StyledUploadFile = () => {
  const log = useDataLogging();
  const { enqueueSnackbar } = useSnackbar();
  const trajectory = useAppSelector((state) => state.trajectories.trajectory);
  const [uploadStudyCards, uploadResult] = usePostUploadExecelStudyCardsMutation();
  const { data: pageData } = useGetPagesQuery({
    trajectoryId: trajectory?.id ?? '',
    type: trajectorySectionCodeLookup['3'],
  });
  const pageId = pageData?.find((item) => item.type === trajectorySectionCodeLookup['3'])?.id;

  const handleFileUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!pageId) return;
      const file: File | undefined = e.target.files?.[0];

      log(LogActionTypes.STUDYCARD_UPLOAD_EXCEL_FILE, {
        pageId: pageId,
      });

      if (!file) return;
      const data = new FormData();
      data.append('excelFile', file);
      uploadStudyCards({ pageId: pageId, body: data });
    },
    [pageId, log, uploadStudyCards]
  );

  useEffect(() => {
    if (uploadResult.isSuccess) {
      if (
        uploadResult.data.createdItems.total &&
        uploadResult.data.createdItems.of !== uploadResult.data.createdItems.total
      ) {
        enqueueSnackbar(
          `Almost perfect. We uploaded ${uploadResult.data.createdItems.total} of ${uploadResult.data.createdItems.of}`,
          {
            variant: 'warning',
          }
        );
      } else {
        enqueueSnackbar('Excel file Upload with success.', {
          variant: 'success',
        });
      }
      uploadResult.reset();
    }
  }, [uploadResult, enqueueSnackbar]);

  useEffect(() => {
    if (uploadResult.error) {
      const error = uploadResult.error as FetchBaseQueryError;
      if (Array.isArray(error.data) && error.data[0] === 'error.excel-file-no-data-sheet') {
        enqueueSnackbar(
          "Het Excel-bestand moet studeerkaartjes bevatten op het tabblad met (exact) de naam 'template study cards'.",
          {
            variant: 'error',
            preventDuplicate: false,
          }
        );
      } else {
        enqueueSnackbar('Something went wrong... Please try again later', {
          variant: 'error',
          preventDuplicate: false,
        });
      }

      uploadResult.reset();
    }
  }, [uploadResult, enqueueSnackbar]);

  return (
    <Button component="label" role={undefined} variant="text" tabIndex={-1}>
      <StyledUploadLink>
        + UPLOADEN VIA EXCEL
        <input
          onChange={handleFileUpload}
          multiple={false}
          value=""
          type="file"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          style={{
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: 1,
            overflow: 'hidden',
            position: 'absolute',
            bottom: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: 1,
          }}
        />
      </StyledUploadLink>
    </Button>
  );
};
